import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Crown, LogIn } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import heroMobileImg from '@/assets/hero-membresias-store-mobile.png';

interface FloatingIconProps {
  src: string;
  alt: string;
  className: string;
  glowColor: string;
  yRange: [number, number];
  xRange?: [number, number];
  rotateRange?: [number, number];
  scrollYProgress: ReturnType<typeof useScroll>['scrollYProgress'];
  initialAnimation: {
    opacity: number;
    x?: number;
    y?: number;
    scale?: number;
  };
  floatAnimation: {
    y?: number[];
    x?: number[];
    rotate?: number[];
    scale?: number[];
  };
  floatTransition: {
    duration: number;
    delay?: number;
    repeat: number;
    ease: string;
  };
  imageSize: string;
  borderRadius: string;
}

const FloatingIcon: React.FC<FloatingIconProps> = ({
  src,
  alt,
  className,
  glowColor,
  yRange,
  xRange = [0, 0],
  rotateRange = [0, 0],
  scrollYProgress,
  initialAnimation,
  floatAnimation,
  floatTransition,
  imageSize,
  borderRadius,
}) => {
  const y = useTransform(scrollYProgress, [0, 1], yRange);
  const x = useTransform(scrollYProgress, [0, 1], xRange);
  const rotate = useTransform(scrollYProgress, [0, 1], rotateRange);

  return (
    <motion.div
      initial={{ opacity: 0, ...initialAnimation }}
      animate={{ opacity: initialAnimation.opacity }}
      transition={{ duration: 1.5, delay: 0.5 }}
      className={className}
      style={{ y, x, rotate }}
    >
      <motion.div
        animate={floatAnimation}
        transition={{ ...floatTransition, repeat: Infinity, ease: "easeInOut" }}
        className="relative"
      >
        <div className={`absolute inset-0 ${glowColor} ${borderRadius} blur-lg md:blur-xl`} />
        <img
          src={src}
          alt={alt}
          className={`relative ${imageSize} ${borderRadius} shadow-[0_0_20px_${glowColor.replace('/30', '/0.4')}] md:shadow-[0_0_30px_${glowColor.replace('/30', '/0.4')}] border ${glowColor.replace('bg-', 'border-')}`}
        />
      </motion.div>
    </motion.div>
  );
};

interface HeroSectionWithParallaxProps {
  t: {
    heroOffer: string;
    heroTitle1: string;
    heroTitle2: string;
    heroTitle3: string;
    heroHighlight: string;
    countdownLabel: string;
    days: string;
    hours: string;
    min: string;
    sec: string;
    priceFrom: string;
    perMonth: string;
    ctaButton: string;
    ctaRegister: string;
    ctaLogin: string;
  };
  timeLeft: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
  plans: Array<{
    id: string;
    name: string;
    logo: string;
  }>;
  scrollToPlans: () => void;
}

export const HeroSectionWithParallax: React.FC<HeroSectionWithParallaxProps> = ({
  t,
  timeLeft,
  plans,
  scrollToPlans,
}) => {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });

  // Parallax transforms - only apply on desktop
  const y1 = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [0, -80]);
  const y2 = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [0, -120]);
  const y3 = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [0, -60]);
  const y4 = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [0, -100]);
  const y5 = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [0, 80]);
  const y6 = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [0, 60]);
  const y7 = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [0, -40]);
  const y8 = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [0, 40]);
  
  const x1 = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [0, -30]);
  const x2 = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [0, 30]);
  const x3 = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [0, -20]);
  const x4 = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [0, 20]);

  return (
    <section ref={sectionRef} className="relative overflow-hidden">
      
      {/* MÓVIL: Imagen hero en la parte superior */}
      <img
        src={heroMobileImg}
        alt="VillaTech Membresía"
        className="w-full h-auto md:hidden"
      />
      
      {/* Content wrapper with transparent background to show page glow */}
      <div className="relative py-6 md:py-32">
        {/* Subtle gradient overlay - doesn't hide global glow */}
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/10 via-transparent to-blue-900/5" />
        
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-blue-500/10 rounded-full blur-[150px] hidden md:block" />
      
      {/* Floating Product Images with Parallax - Tablet and Desktop */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
        {/* Gaming Headphones - Top Left */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 0.5, x: 0 }}
          transition={{ duration: 1.5, delay: 0.5 }}
          style={{ y: y1, x: x1 }}
          className="absolute top-[5%] left-[2%] md:top-[10%] md:left-[5%]"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-blue-500/30 rounded-xl md:rounded-2xl blur-lg md:blur-xl" />
            <img
              src="https://images.unsplash.com/photo-1599669454699-248893623440?w=150&h=150&fit=crop"
              alt="Gaming Headphones"
              className="relative w-14 md:w-28 lg:w-40 rounded-xl md:rounded-2xl shadow-[0_0_20px_rgba(59,130,246,0.4)] md:shadow-[0_0_30px_rgba(59,130,246,0.4)] border border-blue-500/30"
            />
          </div>
        </motion.div>
        
        {/* Phone Case - Top Right */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 0.5, x: 0 }}
          transition={{ duration: 1.5, delay: 0.7 }}
          style={{ y: y2, x: x2 }}
          className="absolute top-[8%] right-[2%] md:top-[15%] md:right-[8%]"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-amber-500/30 rounded-xl md:rounded-2xl blur-lg md:blur-xl" />
            <img
              src="https://images.unsplash.com/photo-1609081219090-a6d81d3085bf?w=150&h=150&fit=crop"
              alt="Phone Case"
              className="relative w-12 md:w-24 lg:w-36 rounded-xl md:rounded-2xl shadow-[0_0_20px_rgba(245,158,11,0.4)] md:shadow-[0_0_30px_rgba(245,158,11,0.4)] border border-amber-500/30"
            />
          </div>
        </motion.div>
        
        {/* Alexa Echo - Middle Left */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 0.4, y: 0 }}
          transition={{ duration: 1.5, delay: 0.9 }}
          style={{ y: y3, x: x3 }}
          className="absolute top-[40%] left-[0%] md:top-[45%] md:left-[3%] hidden sm:block"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-purple-500/30 rounded-xl md:rounded-2xl blur-lg md:blur-xl" />
            <img
              src="https://images.unsplash.com/photo-1543512214-318c7553f230?w=150&h=150&fit=crop"
              alt="Alexa Echo"
              className="relative w-14 md:w-28 lg:w-36 rounded-xl md:rounded-2xl shadow-[0_0_20px_rgba(168,85,247,0.4)] md:shadow-[0_0_30px_rgba(168,85,247,0.4)] border border-purple-500/30"
            />
          </div>
        </motion.div>
        
        {/* Smart Watch - Middle Right */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 0.4, y: 0 }}
          transition={{ duration: 1.5, delay: 1.1 }}
          style={{ y: y4, x: x4 }}
          className="absolute top-[45%] right-[0%] md:top-[50%] md:right-[5%] hidden sm:block"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-green-500/30 rounded-full blur-lg md:blur-xl" />
            <img
              src="https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=150&h=150&fit=crop"
              alt="Smart Watch"
              className="relative w-12 md:w-24 lg:w-32 rounded-full shadow-[0_0_20px_rgba(34,197,94,0.4)] md:shadow-[0_0_30px_rgba(34,197,94,0.4)] border border-green-500/30"
            />
          </div>
        </motion.div>
        
        {/* Power Bank - Bottom Left */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 0.35, x: 0 }}
          transition={{ duration: 1.5, delay: 1.3 }}
          style={{ y: y5, x: x1 }}
          className="absolute bottom-[15%] left-[3%] md:bottom-[20%] md:left-[10%]"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-cyan-500/30 rounded-lg md:rounded-xl blur-lg md:blur-xl" />
            <img
              src="https://images.unsplash.com/photo-1609091839311-d5365f9ff1c5?w=150&h=150&fit=crop"
              alt="Power Bank"
              className="relative w-12 md:w-24 lg:w-32 rounded-lg md:rounded-xl shadow-[0_0_20px_rgba(6,182,212,0.4)] md:shadow-[0_0_30px_rgba(6,182,212,0.4)] border border-cyan-500/30"
            />
          </div>
        </motion.div>
        
        {/* USB Cable - Bottom Right */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 0.35, x: 0 }}
          transition={{ duration: 1.5, delay: 1.5 }}
          style={{ y: y6, x: x2 }}
          className="absolute bottom-[18%] right-[5%] md:bottom-[25%] md:right-[12%]"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-orange-500/30 rounded-lg md:rounded-xl blur-lg md:blur-xl" />
            <img
              src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=150&h=150&fit=crop"
              alt="USB Cable"
              className="relative w-10 md:w-20 lg:w-28 rounded-lg md:rounded-xl shadow-[0_0_20px_rgba(249,115,22,0.4)] md:shadow-[0_0_30px_rgba(249,115,22,0.4)] border border-orange-500/30"
            />
          </div>
        </motion.div>
        
        {/* Fire TV Stick - Top Center Left */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.3, scale: 1 }}
          transition={{ duration: 1.5, delay: 1.7 }}
          style={{ y: y7 }}
          className="absolute top-[30%] left-[15%] md:top-[25%] md:left-[20%] hidden sm:block lg:block"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-red-500/30 rounded-lg md:rounded-xl blur-lg md:blur-xl" />
            <img
              src="https://images.unsplash.com/photo-1593359677879-a4bb92f829d1?w=120&h=120&fit=crop"
              alt="Fire TV"
              className="relative w-10 md:w-20 lg:w-24 rounded-lg md:rounded-xl shadow-[0_0_20px_rgba(239,68,68,0.4)] md:shadow-[0_0_30px_rgba(239,68,68,0.4)] border border-red-500/30"
            />
          </div>
        </motion.div>
        
        {/* Gaming Mouse - Bottom Center */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 0.3, y: 0 }}
          transition={{ duration: 1.5, delay: 1.9 }}
          style={{ y: y8 }}
          className="absolute bottom-[8%] left-[40%] md:bottom-[10%] md:left-[35%] hidden sm:block lg:block"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-pink-500/30 rounded-lg md:rounded-xl blur-lg md:blur-xl" />
            <img
              src="https://images.unsplash.com/photo-1527814050087-3793815479db?w=120&h=120&fit=crop"
              alt="Gaming Mouse"
              className="relative w-10 md:w-20 lg:w-24 rounded-lg md:rounded-xl shadow-[0_0_20px_rgba(236,72,153,0.4)] md:shadow-[0_0_30px_rgba(236,72,153,0.4)] border border-pink-500/30"
            />
          </div>
        </motion.div>
      </div>
      


      <div className="container mx-auto px-6 relative z-10">

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mx-auto"
        >

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            <span className="text-white">{t.heroTitle1}</span>
            <br />
            <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
              {t.heroTitle2}
            </span>
            <br />
            <span className="text-white">{t.heroTitle3}</span>
          </h1>

          <p className="text-xl md:text-2xl text-zinc-300 mb-4 max-w-3xl mx-auto">
            Recibí <span className="text-blue-400 font-bold">{t.heroHighlight}</span> y descuentos en nuestra tienda
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-6">
            <Button
              onClick={scrollToPlans}
              size="lg"
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold text-lg md:text-xl px-8 md:px-10 py-6 md:py-7 rounded-2xl shadow-[0_0_40px_rgba(59,130,246,0.4)] hover:shadow-[0_0_60px_rgba(59,130,246,0.6)] transition-all duration-300"
            >
              <CreditCard className="w-5 h-5 md:w-6 md:h-6 mr-2" />
              {t.ctaRegister || 'Activar Membresía'}
            </Button>
            <Button
              onClick={() => navigate('/login?force=1')}
              size="lg"
              variant="outline"
              className="border-2 border-blue-500/50 bg-zinc-900/80 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 font-bold text-lg md:text-xl px-8 md:px-10 py-6 md:py-7 rounded-2xl transition-all duration-300"
            >
              <LogIn className="w-5 h-5 md:w-6 md:h-6 mr-2" />
              {t.ctaLogin || 'Iniciar Sesión'}
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <div className="px-4 py-2">
              <span className="text-lg md:text-xl font-semibold text-zinc-300">{t.priceFrom} ₡12.000{t.perMonth}</span>
            </div>
          </div>

          {/* Logos Preview */}
          <div className="flex justify-center gap-8 mt-12 mb-10">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="text-center"
              >
                <img 
                  src={plan.logo} 
                  alt={`Logo ${plan.name}`}
                  className="w-16 h-16 md:w-20 md:h-20 object-contain mx-auto"
                />
                <p className="text-sm text-zinc-400 mt-2">{plan.name}</p>
              </motion.div>
            ))}
          </div>

          {/* COUNTDOWN TIMER - debajo de los logos */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8"
          >
            <p className="text-zinc-400 text-sm mb-3">{t.countdownLabel}</p>
            <div className="flex justify-center gap-3 md:gap-4">
              {[
                { value: timeLeft.days, label: t.days },
                { value: timeLeft.hours, label: t.hours },
                { value: timeLeft.minutes, label: t.min },
                { value: timeLeft.seconds, label: t.sec },
              ].map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  className="bg-gradient-to-b from-zinc-800 to-zinc-900 border border-blue-500/30 rounded-xl px-4 py-3 md:px-6 md:py-4 min-w-[70px] md:min-w-[90px] shadow-[0_0_20px_rgba(59,130,246,0.15)]"
                >
                  <motion.div
                    key={item.value}
                    initial={{ scale: 1.2 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.2 }}
                    className="text-2xl md:text-4xl font-bold text-blue-400"
                  >
                    {String(item.value).padStart(2, '0')}
                  </motion.div>
                  <div className="text-xs md:text-sm text-zinc-400">{item.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
      </div>
    </section>
  );
};

export default HeroSectionWithParallax;
